import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';

import { extractErrorMessage } from '@/lib/api';
import { getErrorLogs, setErrorLogStatus } from '../services/error-log.service';

export function useErrorLogs(page: number, source?: string) {
  return useQuery({
    queryKey: ['error-logs', page, source ?? null],
    queryFn: () => getErrorLogs(page, source),
    placeholderData: keepPreviousData,
  });
}

/**
 * Spunta "visto" / "gestito" su una riga del log. Invalida il prefix
 * ['error-logs'], quindi anche il badge sidebar (['error-logs','today-count'])
 * si aggiorna subito quando un errore di oggi viene gestito.
 */
export function useSetErrorLogStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...flags }: { id: string; seen?: boolean; handled?: boolean }) =>
      setErrorLogStatus(id, flags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-logs'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}

/**
 * Stessa spunta su più righe insieme (selezione nella lista). Una PATCH per
 * riga, in parallelo: la pagina ne mostra al massimo 50.
 */
export function useBulkSetErrorLogStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, ...flags }: { ids: string[]; seen?: boolean; handled?: boolean }) =>
      Promise.all(ids.map((id) => setErrorLogStatus(id, flags))),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['error-logs'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });
}
